import { IServiceFactoryExtended } from '../service-factory-extended.interface';
import { IProgressiveWebAppService } from "./progressive-web-app.service.interface";
import { Check } from '@sparkware/app-utils';
import { observable, action, computed, makeObservable } from "mobx";
import { ServiceBase } from '../ServiceBase';
import { DomEventSubscription } from '../../../utils/event-listener-registration';
import {
    PWA_INSTALLED_CALLBACK, PWA_SHOULD_AUTO_POPUP_LOCAL_STORAGE_KEY,
    PWA_PROMPT_CALLBACK,
    PWA_PROMPT_EVENT,
} from "../../../common/const/service-worker-constants";
import {OrbitUserFlowAnalyticsCategory} from "../analytics/analytics.service.interface";


export class ProgressiveWebAppService extends ServiceBase implements IProgressiveWebAppService {

    @observable.ref
    private _onBeforeInstallEvent = null;

    private _clickSubscription: DomEventSubscription;

    @computed
    public get isPWAInstallable(): boolean {
        return Check.isFunction(this._onBeforeInstallEvent?.prompt);
    }

    constructor(services: IServiceFactoryExtended) {
        super(services);

        makeObservable(this);

        const pwaEvent = this.services.window.getGlobalProperty(PWA_PROMPT_EVENT);
        if (pwaEvent) {
            // this means beforeinstallprompt handler was already called
            this._init(pwaEvent);
        }

        // beforeinstallprompt can arrive after service constructor finishes
        // and also can be called multiple times

        // register beforeinstallprompt callback
        this.services.window.setGlobalProperty(PWA_PROMPT_CALLBACK, (pwaEvent) => {
            this._init(pwaEvent);
        });

        // register appinstalled callback
        this.services.window.setGlobalProperty(PWA_INSTALLED_CALLBACK, () => {
            this._onAppInstalled();
        });

        this.reaction(() => {
                return {
                    isAuthorized: this.services.user.isAuthorized,
                    isCstatus: this.services.cmsSegmentation.isCstatus,
                    isUKOrIrl: this.services.cmsSegmentation.isUKOrIrl
                }
            }, data => {
                if (data.isAuthorized) {
                    const shouldAutoPopup = data.isCstatus && !data.isUKOrIrl;
                    this.services.localStorage.setItem(PWA_SHOULD_AUTO_POPUP_LOCAL_STORAGE_KEY, shouldAutoPopup);
                }
            },
            {
                fireImmediately: true
            }
        );

        this._subscribeToMainScreenClick();

        // add to analytics PWA launch event
        if ((this.services.device.isStandalone && this.services.device.browser.isSafari) || this.services.device.isProgressiveWebApp) {
            this.services.analytics.sendOrbitUserFlowEvent(OrbitUserFlowAnalyticsCategory.PWA, 'Launch', this.services.user.cid);
        }
    }

    @action.bound
    // can be called multiple times by beforeinstallprompt handler
    private _init(onBeforeInstallEvent: any) {
        // disable it for hybrid and mini Orbit
        if (this.services.miniOrbit.isEnabled || this.services.device.hybrid.isHybrid) {
            return;
        }

        this._onBeforeInstallEvent = onBeforeInstallEvent;
    }

    private _subscribeToMainScreenClick() {
        this._clickSubscription = this.services.dom.subscribeTo.click(this._tryToShowPwaInstall);
    }

    private _unsubscribeFromMainScreenClick() {
        if (this._clickSubscription) {
            this._clickSubscription.dispose();
            this._clickSubscription = null;
        }
    }

    @action.bound
    private _onAppInstalled() {
        this.services.analytics.sendOrbitUserFlowEvent(OrbitUserFlowAnalyticsCategory.PWA, 'Browser_Prompt', 'INSTALL');
        this.services.logger.debug('PWA installed');
        this.services.window.setGlobalProperty(PWA_PROMPT_CALLBACK, null);
        this._onBeforeInstallEvent = null;
    }

    private _shouldAutoPrompt(): boolean {
        const shouldPrompt = this.services.localStorage.getItem(PWA_SHOULD_AUTO_POPUP_LOCAL_STORAGE_KEY) === 'true' && !this.services.cmsSegmentation.isAppBannerUKorIRL;
        return this.isPWAInstallable
            && shouldPrompt
            && !this.services.user.isAuthorized;
    }

    private _tryToShowPwaInstall = () => {
        if (this._shouldAutoPrompt()) {
            this.prompt().catch(err => {
                this.services.logger.error('Attempt to show PWA prompt failed', err);
            });
        }
    }

    prompt(): Promise<void> {
        if (!this.isPWAInstallable) {
            return Promise.reject("Not a PWA");
        }

        try {
            return this._onBeforeInstallEvent.prompt()
                .then(res => {
                    this._unsubscribeFromMainScreenClick();
                    this.services.analytics.sendOrbitUserFlowEvent(OrbitUserFlowAnalyticsCategory.PWA, 'Browser_Prompt', 'SHOW');
                    this.services.logger.debug(`PWA prompt() success with outcome: ${res && res.outcome}`);
                    return res;
                });
        }
        catch (err) {
            this.services.logger.error('Pwa exception caught', err);
            throw err;
        }
    }
}
